import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apiFetch, getCountries } from "../../../utils/api";

export const usePerspectives = () => {
  const [params, setParams] = useSearchParams();
  const [countries, setCountries] = useState([]), [country, setCountry] = useState(params.get("country") || "");
  const [topic, setTopic] = useState(params.get("topic") || ""), [chosenTopic, setChosenTopic] = useState(null);
  const [data, setData] = useState(null), [busy, setBusy] = useState(false), [error, setError] = useState(""), [locked, setLocked] = useState(false);
  const serial = useRef(0), controller = useRef(null), deadline = useRef(0);
  const queryCountry = params.get("country") || "", queryTopic = params.get("topic") || "";
  const stop = useCallback(() => { serial.current += 1; controller.current?.abort(); deadline.current = 0; setBusy(false); }, []);
  const read = useCallback(async (c, t) => {
    controller.current?.abort(); controller.current = new AbortController(); const sequence = ++serial.current;
    setBusy(true); setError(""); setLocked(false);
    try {
      const result = await apiFetch(`/api/perspectives/briefing?country=${encodeURIComponent(c)}&topic=${encodeURIComponent(t)}`, { signal: controller.current.signal });
      if (sequence !== serial.current) return;
      setData(result); if (result.status === "pending") deadline.current = Date.now() + 240000;
    } catch (err) {
      if (err.name === "AbortError" || sequence !== serial.current) return;
      setLocked(err.status === 402 || err.status === 403); setError(err.status === 402 || err.status === 403 ? "" : typeof err.data?.detail === "string" ? err.data.detail : "The country briefing could not be loaded. Please try again.");
    } finally { if (sequence === serial.current) setBusy(false); }
  }, []);
  useEffect(() => { let active = true; getCountries().then(value => { if (active) setCountries(value.countries || []); }).catch(() => { if (active) setError("Countries could not be loaded. Please reload this page."); }); return () => { active = false; }; }, []);
  useEffect(() => {
    setCountry(queryCountry); setTopic(queryTopic); setData(null); setLocked(false);
    if (queryCountry && queryTopic) read(queryCountry, queryTopic);
    else stop();
  }, [queryCountry, queryTopic, read, stop]);
  useEffect(() => {
    if (!topic) { setChosenTopic(null); return; }
    let active = true;
    apiFetch(`/api/perspectives/topics/${encodeURIComponent(topic)}`).then(value => { if (active) setChosenTopic(value); }).catch(() => { if (active) setChosenTopic(null); });
    return () => { active = false; };
  }, [topic]);
  useEffect(() => () => { serial.current += 1; controller.current?.abort(); }, []);
  useEffect(() => {
    if (data?.status !== "pending") return;
    const sequence = serial.current;
    const timer = window.setTimeout(async () => {
      if (Date.now() > deadline.current) { setData(old => ({ ...old, status: "failed", can_prepare: true, error: "Preparation is taking longer than expected. Load the briefing again to check its status." })); return; }
      try {
        const result = await apiFetch(`/api/perspectives/briefing?country=${encodeURIComponent(country)}&topic=${encodeURIComponent(topic)}`);
        if (sequence === serial.current) setData(result.status === "not_ready" ? { ...result, status: "pending" } : result);
      } catch (err) { if (sequence === serial.current) setData(old => ({ ...old, status: "failed", error: err.status === 402 ? "Your subscription is required to continue reading." : "The status could not be checked. Load the briefing again.", can_prepare: false })); }
    }, 2500);
    return () => window.clearTimeout(timer);
  }, [data, country, topic]);
  const changeCountry = value => { stop(); setCountry(value); setData(null); setError(""); setLocked(false); };
  const changeTopic = value => { stop(); setTopic(typeof value === "string" ? value : value.id); if (typeof value !== "string") setChosenTopic(value); setData(null); setError(""); setLocked(false); };
  const view = event => {
    event?.preventDefault();
    if (!country || !topic) return;
    if (country === queryCountry && topic === queryTopic) read(country, topic);
    else setParams({ country, topic });
  };
  const prepare = async () => {
    if (!country || !topic || busy || data?.status === "pending") return;
    const sequence = ++serial.current; setBusy(true); setError("");
    try {
      const result = await apiFetch("/api/perspectives/briefing/prepare", { method: "POST", body: JSON.stringify({ country, topic }) });
      if (sequence !== serial.current) return;
      deadline.current = Date.now() + 240000; setData(result);
    } catch (err) { if (sequence === serial.current) { setLocked(err.status === 402 || err.status === 403); setError(err.status === 402 || err.status === 403 ? "" : typeof err.data?.detail === "string" ? err.data.detail : "The country briefing could not be prepared. Please try again later."); } }
    finally { if (sequence === serial.current) setBusy(false); }
  };
  return { countries, country, topic, chosenTopic, data, busy, error, locked, changeCountry, changeTopic, view, prepare };
};